import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import SuccessIcon from '@mui/icons-material/TaskAlt';
import { Button, Grid, Paper, Stack, Typography } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { useWallet } from '@solana/wallet-adapter-react';
import { useWalletModal } from '@solana/wallet-adapter-react-ui';
import axios from 'axios';
import { FC, useCallback, useEffect, useState } from 'react';
import naclUtil from 'tweetnacl-util';
import { formatAddress } from './utils/format';

// const BACKEND_URL = 'http://localhost:5001/meltlabs-spl/us-central1';
const BACKEND_URL = 'https://us-central1-meltlabs-spl.cloudfunctions.net';
const DISCORD_URL = 'https://discord.com/api/oauth2';

const App: FC = () => {
  const [verifying, setVerifying] = useState(false);
  const [done, setDone] = useState(false);
  const [solAddress, setSolAddress] = useState('');
  const [error, setError] = useState<string>();
  const [discordUser, setDiscordUser] = useState<any>();

  const { setVisible } = useWalletModal();
  const wallet = useWallet();
  wallet.autoConnect = false;

  const getDiscordUser = useCallback(async (accessToken: string) => {
    const getUserResult = await axios.get(`${DISCORD_URL}/@me`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    setDiscordUser(getUserResult.data.user);
  }, []);

  const connectSolana = useCallback(
    () => (wallet.wallet ? wallet.connect().catch() : setVisible(true)),
    [wallet, setVisible]
  );

  const signSolana = useCallback(async () => {
    setVerifying(true);
    setSolAddress(wallet.publicKey!.toString());

    const getNonceResponse = await axios.post(`${BACKEND_URL}/getNonce`, {
      address: wallet.publicKey?.toString(),
      network: 'solana',
    });
    const { nonce } = getNonceResponse.data;
    const signedMessage = await (window as any).solana.signMessage(
      naclUtil.decodeUTF8(nonce),
      'utf8'
    );
    return signedMessage;
  }, [wallet]);

  useEffect(() => {
    if (!wallet.publicKey) {
      return;
    }
    setSolAddress(wallet.publicKey.toString());
    const accessToken = window.location.hash
      ? window.location.hash.split('&')[1].split('=')[1]
      : '';
    const discordServerId = window.location.hash
      ? window.location.hash.split('&')[4].split('=')[1]
      : [''];
    signSolana().then((signedMessage) => {
      axios
        .post(`${BACKEND_URL}/verify`, {
          address: wallet.publicKey?.toBase58(),
          network: 'solana',
          accessToken,
          discordServerId,
          nearWallet: (window as any).accountId,
          signature: naclUtil.encodeBase64(signedMessage.signature),
          publicKey: naclUtil.encodeBase64(
            wallet.publicKey?.toBuffer() as Buffer
          ),
        })
        .then(() => setDone(true))
        .catch((error) => setError('verification failed.'))
        .finally(() => setVerifying(false));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wallet.connected]);

  useEffect(() => {
    const accessToken = window.location.hash
      ? window.location.hash.split('&')[1].split('=')[1]
      : '';
    const discordServerId = window.location.hash
      ? window.location.hash.split('&')[4].split('=')[1]
      : [''];
    if (accessToken === '' || discordServerId === '') {
      setError('invalid discord authentication');
      return;
    }
    getDiscordUser(accessToken);
  }, [getDiscordUser]);

  const AppWrapper: FC = ({ children }) => {
    return (
      <Grid
        container
        spacing={0}
        direction="column"
        alignItems="center"
        justifyContent="center"
        style={{ minHeight: '100vh' }}
      >
        <Grid item xs={3}>
          <Typography variant="h2" className="px-4">
            Fort bot
          </Typography>
          <Paper className="p-8 mx-4 rounded-lg">
            <Header />
            {children}
          </Paper>
        </Grid>
        <div className="fixed bottom-0 left-0 right-0 flex items-center justify-center w-full h-12 text-sm text-center text-white bg-black">
          made with ❤ by&nbsp;
          <a href="https://twitter.com/flofi_dev">flofi</a>&nbsp;&&nbsp;
          <a href="https://twitter.com/ryuk_dev">ryuk</a>&nbsp;|&nbsp;
          <a href="https://www.ryuk.dev">https://www.ryuk.dev</a>
        </div>
      </Grid>
    );
  };

  interface HeaderProps {}
  const Header: FC<HeaderProps> = (props) => {
    return (
      <div className="flex items-center justify-center mb-4">
        <img
          src={`https://cdn.discordapp.com/avatars/${discordUser?.id}/${discordUser?.avatar}.png`}
          className="rounded-lg w-[5rem] h-[5rem] border-2"
          alt=""
        />
        <div className="w-6 border-t-2 border-dotted"></div>
        <img
          src="fort.png"
          alt=""
          className="rounded-lg w-[7rem] h-[7rem] border-2"
        />
        <div className="w-6 border-t-2 border-dotted"></div>
        <img
          src="meltlabs.jpg"
          alt=""
          className="rounded-lg w-[5rem]  h-[5rem] border-2"
        />
      </div>
    );
  };

  if (error) {
    return (
      <AppWrapper>
        <Stack direction="row" alignItems="center" gap={1} className="mt-4">
          <ErrorOutlineIcon color="error" sx={{ fontSize: '50px' }} />
          <Typography color="error" variant="h6" className="font-bold">
            {error}
          </Typography>
        </Stack>
      </AppWrapper>
    );
  }

  if (done) {
    return (
      <AppWrapper>
        <div className="flex flex-col items-center text-center">
          <SuccessIcon color="primary" className="mb-4 text-9xl" />
          <Typography>
            verification done <br /> you can close this window and return to
            discord
          </Typography>
        </div>
      </AppWrapper>
    );
  }

  return (
    <AppWrapper>
      <Typography variant="h6" className="py-8 text-center">
        Connect your Solana-Wallet
        <br /> to verify for Discord
      </Typography>
      {solAddress && (
        <Typography variant="body1" className="text-center">
          Wallet: {formatAddress(solAddress)}
        </Typography>
      )}
      {verifying === false ? (
        <div className="flex justify-center">
          <Button variant="outlined" onClick={connectSolana}>
            Verify with Solana
          </Button>
        </div>
      ) : (
        <div className="flex flex-col items-center mt-5">
          <CircularProgress size={70} />
          <Typography className="mt-5">verifying your nft...</Typography>
        </div>
      )}
    </AppWrapper>
  );
};

export default App;
